import { Card, Flex, Group, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import { Stimulus, StimulusResponse } from "types/testTypes";
import { groupStimuli, groupStimulusResponses } from "@utils/TestUtils";

import CrossShape from "@components/common/shapes/CrossShape";
import React from "react";
import { ScatterChart } from "@mantine/charts";
import { useTranslation } from "react-i18next";

interface CPTStimuliAndResponseComponentProps {
  stimuli?: Stimulus[] | null;
  responses?: StimulusResponse[] | null;
  style?: React.CSSProperties;
  withLegend?: boolean;
  /** Stage boundary times (seconds, same axis as stimulus timestamps) drawn as vertical dividers. */
  stageBoundaries?: number[];
}

const CPTStimuliAndResponseComponent: React.FC<CPTStimuliAndResponseComponentProps> = ({
  stimuli,
  responses,
  style,
  withLegend = true,
  stageBoundaries,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const { target, nonTarget, visualDistractor, audioDistractor } = groupStimuli(stimuli);

  const {
    correctResponse,
    correctMultiPressResponse,
    wrongResponse,
    wrongMultiPressResponse,
    missResponse,
    falsePositiveEmptyResponse,
    falsePositiveAudioResponse,
    falsePositiveVisualResponse,
    falsePositiveAudioVisualResponse,
  } = groupStimulusResponses(responses);

  const CustomShape = (props) => {
    return (
      <line
        x1={props.x1}
        y1={props.y1 + 5}
        x2={props.x1}
        y2={props.y1 + props.height + 5}
        stroke={props.color}
        strokeWidth={props.strokeWidth}
      />
    );
  };

  const dataKey = { x: "time", y: "delta", z: "value" };
  const referenceLines = [];
  (stageBoundaries ?? []).forEach((time) => {
    referenceLines.push({
      x: time,
      color: "gray.5",
      strokeDasharray: "4 4",
      ifOverflow: "extendDomain",
    });
  });
  target.forEach((stimulus) => {
    referenceLines.push({
      x: stimulus.timeStamp,
      color: "green",
      height: 10,
      strokeWidth: 2.5,
      shape: CustomShape,
      ifOverflow: "extendDomain",
    });
  });
  nonTarget.forEach((stimulus) => {
    referenceLines.push({
      x: stimulus.timeStamp,
      color: "#868e96",
      height: 10,
      strokeWidth: 2.5,
      shape: CustomShape,
      ifOverflow: "extendDomain",
    });
  });
  visualDistractor.forEach((stimulus) => {
    referenceLines.push({
      x: stimulus.timeStamp,
      color: "rgba(0, 0, 0, 0.5)",
      height: 4,
      zIndex: -10,
      strokeWidth: 2,
      shape: CustomShape,
      ifOverflow: "extendDomain",
    });
  });
  audioDistractor.forEach((stimulus) => {
    referenceLines.push({
      x: stimulus.timeStamp,
      color: "rgba(0, 0, 0, 0.5)",
      height: 4,
      zIndex: -10,
      strokeWidth: 2,
      shape: CustomShape,
      ifOverflow: "extendDomain",
    });
  });

  const combinedData = [
    {
      name: t("extraTests.continuousPerformance.correctResponse"),
      color: "green",
      data: correctResponse,
    },
    {
      name: t("extraTests.continuousPerformance.correctMultiPressResponse"),
      color: "teal.6",
      data: correctMultiPressResponse,
    },
    {
      name: t("extraTests.continuousPerformance.incorrectResponse"),
      color: "red.8",
      data: wrongResponse,
    },
    {
      name: t("extraTests.continuousPerformance.wrongMultiPressResponse"),
      color: "red",
      data: wrongMultiPressResponse,
    },
    {
      name: t("extraTests.continuousPerformance.missResponse"),
      color: "orange",
      data: missResponse,
    },
    {
      name: t("extraTests.continuousPerformance.falsePositiveEmptyResponse"),
      color: "yellow",
      data: falsePositiveEmptyResponse,
      shape: CrossShape,
    },
    {
      name: t("extraTests.continuousPerformance.falsePositiveAudioResponse"),
      color: "yellow.8",
      data: falsePositiveAudioResponse,
      shape: CrossShape,
    },
    {
      name: t("extraTests.continuousPerformance.falsePositiveVisualResponse"),
      color: "yellow.6",
      data: falsePositiveVisualResponse,
      shape: CrossShape,
    },
    {
      name: t("extraTests.continuousPerformance.falsePositiveAudioVisualResponse"),
      color: "yellow.4",
      data: falsePositiveAudioVisualResponse,
      shape: CrossShape,
    },
  ];

  return (
    <Card withBorder radius="md" shadow="md" p="md">
      <Stack gap="xs">
        <Title order={6} ta="start">
          {t("extraTests.continuousPerformance.stimuliAndResponsesChartTitle")}
        </Title>

        <ScatterChart
          style={{ ...style }}
          data={combinedData}
          dataKey={dataKey}
          h={theme.spacing.longChart}
          scatterProps={{
            shape: (props) => {
              if (props.payload?.delta === 0) {
                return <CrossShape {...props} />;
              }
              return <circle r={2.5} cx={props.cx} cy={props.cy} fill={props.fill} />;
            },
          }}
          withYAxis={true}
          valueFormatter={(value) => `${value} s`}
          referenceLines={referenceLines}
          yAxisProps={{
            domain: [0, 2],
          }}
        />
        {withLegend && (
          <Flex justify={"flex-end"} gap="md" wrap="wrap">
            {combinedData
              .filter((series) => series.data.length > 0)
              .map((series) => (
                <Group key={series.name} wrap="nowrap" gap={"sm"}>
                  {series.shape === CrossShape ? (
                    <CrossShape size={12} />
                  ) : (
                    <Text
                      c={series.color}
                      span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "currentColor",
                        display: "inline-block",
                      }}
                    />
                  )}
                  <Text size="sm">{series.name}</Text>
                </Group>
              ))}
          </Flex>
        )}
      </Stack>
    </Card>
  );
};

export default CPTStimuliAndResponseComponent;
